/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag Fragment
 */

import { Fragment, type MouseEvent, useCallback, useEffect, useRef, useState } from 'react';

import { jsx } from '@compiled/react';
import {
	defineMessages,
	FormattedDate,
	FormattedMessage,
	FormattedRelativeTime,
	useIntl,
} from 'react-intl';

import Button from '@atlaskit/button/new';
import { css, cssMap } from '@atlaskit/css';
import CopyIcon from '@atlaskit/icon/core/copy';
import LinkIcon from '@atlaskit/icon/core/link';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import Image from '@atlaskit/image';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import { Card } from '@atlaskit/smart-card';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import { AppIcon } from '../app-icon/app-icon';
import errorImageDark from '../assets/errorImageDark.svg';
import errorImageLight from '../assets/errorImageLight.svg';
import liveReportingGraphic from '../assets/liveReportingGraphic.svg';

import { getAppFromSource } from './utils';

export const messages: {
	copied: {
		defaultMessage: string;
		description: string;
		id: string;
	};
	copy: {
		defaultMessage: string;
		description: string;
		id: string;
	};
	copyTooltip: {
		defaultMessage: string;
		description: string;
		id: string;
	};
	embedError: {
		defaultMessage: string;
		description: string;
		id: string;
	};
	modified: {
		defaultMessage: string;
		description: string;
		id: string;
	};
	modifiedBy: {
		defaultMessage: string;
		description: string;
		id: string;
	};
	open: {
		defaultMessage: string;
		description: string;
		id: string;
	};
	openLink: {
		defaultMessage: string;
		description: string;
		id: string;
	};
} = defineMessages({
	modified: {
		id: 'smart-knowledge-cards.link-embed.modified',
		defaultMessage: 'Modified ',
		description:
			'Text label shown inline before the relative time in the link embed card footer, indicating when the document was last modified.',
	},
	modifiedBy: {
		id: 'smart-knowledge-cards.link-embed.modified-by',
		defaultMessage: ' by {author}',
		description: 'Text showing who last modified the document',
	},
	open: {
		id: 'smart-knowledge-cards.link-embed.open',
		defaultMessage: 'Open',
		description: 'Button label to open the link in a new tab',
	},
	copy: {
		id: 'smart-knowledge-cards.link-embed.copy',
		defaultMessage: 'Copy',
		description:
			'Text shown on an icon button in the link embed card footer that copies the link URL to the clipboard when clicked.',
	},
	copyTooltip: {
		id: 'smart-knowledge-cards.link-embed.copy-tooltip',
		defaultMessage: 'Copy link',
		description:
			'Text shown in the tooltip that appears when hovering over the copy icon button in the link embed card footer.',
	},
	copied: {
		id: 'smart-knowledge-cards.link-embed.copied',
		defaultMessage: 'Link copied!',
		description: 'Tooltip label shown after the link has been copied to clipboard',
	},
	openLink: {
		id: 'smart-knowledge-cards.link-embed.open-link',
		defaultMessage: 'Open link',
		description:
			'Text shown in the tooltip that appears when hovering over the open link icon button in the link embed card footer, which opens the linked resource in a new tab.',
	},
	embedError: {
		id: 'smart-knowledge-cards.link-embed.embed-error',
		defaultMessage: "We couldn't load this preview",
		description: 'Error message shown when the embed preview fails to load',
	},
});

export interface LinkEmbedVariantProps {
	generatorName: string;
	iconUrl: string;
	isError: boolean;
	isLoading: boolean;
	isOlderThanWeek: boolean | null;
	modifiedBy: string;
	onLinkClick?: (opts: { isAuxClick?: boolean; isContextMenu?: boolean; isCopy?: boolean }) => void;
	previewHref: string;
	provider: string;
	secondsAgo: number;
	title: string;
	updatedDate: Date | null;
	url: string;
}

const iconTileStyles = css({
	backgroundColor: token('color.background.neutral'),
	borderRadius: token('radius.large'),
	width: '32px',
	height: '32px',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	flexShrink: 0,
});

const titleContainerStyles = css({
	flex: 1,
	minWidth: '0px',
	overflow: 'hidden',
});

const embedErrorStyles = cssMap({
	root: {
		position: 'absolute',
		inset: '0',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		gap: token('space.100'),
		backgroundColor: token('elevation.surface.overlay.hovered'),
		borderRadius: token('radius.large'),
	},
});

export const EmbedError = (): JSX.Element => {
	const { formatMessage } = useIntl();
	return (
		<Box xcss={embedErrorStyles.root}>
			<Image
				src={errorImageLight}
				srcDark={errorImageDark}
				role="presentation"
				alt=""
				width={160}
			/>
			<Text size="small" color="color.text.subtlest">
				{formatMessage(messages.embedError)}
			</Text>
		</Box>
	);
};

const embedContainerStyles = css({
	width: '100%',
	marginTop: token('space.150'),
	position: 'relative',
});

const embedContainerUnresolvedStyles = css({
	aspectRatio: '16/9',
});

const liveReportingGraphicStyles = css({
	filter: 'grayscale(100%)',
	opacity: token('opacity.loading'),
	position: 'absolute',
	top: '50%',
	left: '50%',
	transform: 'translate(-50%, -50%)',
});

const overlayStyles = cssMap({
	root: {
		position: 'absolute',
		inset: '0',
		backgroundColor: token('elevation.surface.overlay.hovered'),
		borderRadius: token('radius.large'),
	},
});

const headerSkeletonStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.150'),
	paddingLeft: token('space.100'),
	paddingRight: token('space.100'),
	height: '36px',
	paddingBottom: token('space.050'),
});

const skeletonTextStyles = css({
	display: 'flex',
	flexDirection: 'column',
	flex: 1,
	justifyContent: 'space-between',
	height: '36px',
});

export const LoadingSkeleton = (): JSX.Element => (
	<div css={headerSkeletonStyles}>
		<Skeleton width={36} height={36} borderRadius={token('radius.large')} />
		<div css={skeletonTextStyles}>
			<Skeleton width="40%" height={12} borderRadius={token('radius.small')} />
			<Skeleton width="60%" height={12} borderRadius={token('radius.small')} />
		</div>
	</div>
);

export interface OpenLinkButtonClickOpts {
	isAuxClick?: boolean;
	isContextMenu?: boolean;
	isCopy?: boolean;
}

export interface OpenLinkButtonProps {
	/** Reports primary, middle, and right-click interactions on the open button. */
	onLinkClick?: (opts: OpenLinkButtonClickOpts) => void;
	url: string;
}

export const OpenLinkButton = ({ url, onLinkClick }: OpenLinkButtonProps): JSX.Element => {
	const { formatMessage } = useIntl();

	const trackingEnabled = !!onLinkClick;

	const handlePrimaryClick = useCallback(() => {
		onLinkClick?.({});
		window.open(url, '_blank', 'noopener,noreferrer');
	}, [url, onLinkClick]);

	const handleAuxClick = useCallback(
		(event: MouseEvent<HTMLElement>) => {
			// Middle-click only — left/right click come through onClick/onContextMenu.
			if (event.button !== 1) {
				return;
			}
			event.preventDefault();
			onLinkClick?.({ isAuxClick: true });
			window.open(url, '_blank', 'noopener,noreferrer');
		},
		[url, onLinkClick],
	);

	const handleContextMenu = useCallback(() => {
		// No preventDefault — let the browser show its native menu.
		onLinkClick?.({ isContextMenu: true });
	}, [onLinkClick]);

	return (
		<Tooltip content={formatMessage(messages.openLink)} position="top">
			<Button
				appearance="default"
				iconAfter={LinkExternalIcon}
				onClick={handlePrimaryClick}
				onAuxClick={trackingEnabled ? handleAuxClick : undefined}
				onContextMenu={trackingEnabled ? handleContextMenu : undefined}
			>
				<FormattedMessage {...messages.open} />
			</Button>
		</Tooltip>
	);
};

const COPY_TIMEOUT_DELAY = 2500;

export const CopyLinkButton = ({
	url,
	onLinkClick,
}: {
	onLinkClick?: (opts: OpenLinkButtonClickOpts) => void;
	url: string;
}): JSX.Element => {
	const { formatMessage } = useIntl();
	const [isCopied, setIsCopied] = useState(false);
	const [tooltipLabel, setTooltipLabel] = useState(formatMessage(messages.copyTooltip));
	const updateTooltip = useRef<() => void>();

	useEffect(() => {
		updateTooltip.current?.();
	}, [tooltipLabel]);

	const handleCopy = useCallback(async () => {
		try {
			await navigator.clipboard.writeText(url);
			onLinkClick?.({ isCopy: true });
			setIsCopied(true);
			setTooltipLabel(formatMessage(messages.copied));
			setTimeout(() => {
				setIsCopied(false);
				setTooltipLabel(formatMessage(messages.copyTooltip));
			}, COPY_TIMEOUT_DELAY);
		} catch (_e) {
			// Failed to copy
		}
	}, [url, formatMessage, onLinkClick]);

	return (
		<Tooltip
			content={({ update }) => {
				updateTooltip.current = update;
				return tooltipLabel;
			}}
			position="top"
		>
			<Button appearance="default" iconAfter={CopyIcon} isDisabled={isCopied} onClick={handleCopy}>
				<FormattedMessage {...messages.copy} />
			</Button>
		</Tooltip>
	);
};

export const EmbedPreview = ({
	url,
	previewHref,
	inheritDimensions = false,
	isLoading = false,
}: {
	inheritDimensions?: boolean;
	isLoading?: boolean;
	previewHref: string;
	url: string;
}): JSX.Element | null => {
	const [resolved, setResolved] = useState(false);
	const [cardError, setCardError] = useState(false);
	const handleResolve = useCallback(() => setResolved(true), []);
	const handleError = useCallback(() => setCardError(true), []);

	if (!previewHref && !isLoading) {
		return null;
	}

	const card = (
		<>
			{previewHref && (
				<Card
					appearance="embed"
					url={url}
					frameStyle="hide"
					inheritDimensions={inheritDimensions}
					onResolve={handleResolve}
					onError={handleError}
				/>
			)}
			{cardError ? (
				<EmbedError />
			) : !resolved || !previewHref ? (
				<Box xcss={overlayStyles.root}>
					<img
						src={liveReportingGraphic}
						alt=""
						role="presentation"
						css={liveReportingGraphicStyles}
					/>
				</Box>
			) : null}
		</>
	);

	return inheritDimensions ? (
		card
	) : (
		<div css={[embedContainerStyles, !resolved && embedContainerUnresolvedStyles]}>{card}</div>
	);
};

export const IconTile = ({
	iconUrl,
	isError,
	generatorName,
}: {
	generatorName: string;
	iconUrl: string;
	isError: boolean;
}): JSX.Element => {
	const app = getAppFromSource(generatorName, iconUrl);

	return (
		<div css={iconTileStyles}>
			{generatorName ? (
				<AppIcon app={app} size="xxsmall" />
			) : iconUrl ? (
				<img src={iconUrl} alt="" width={16} height={16} />
			) : isError ? (
				<LinkIcon label="" />
			) : null}
		</div>
	);
};

export const TitleMeta = ({
	title,
	provider,
	updatedDate,
	secondsAgo,
	isOlderThanWeek,
	modifiedBy,
}: Pick<
	LinkEmbedVariantProps,
	'title' | 'provider' | 'updatedDate' | 'secondsAgo' | 'isOlderThanWeek' | 'modifiedBy'
>): JSX.Element => (
	<div css={titleContainerStyles}>
		<Stack space="space.0">
			<Text size="medium" weight="semibold" maxLines={1}>
				{title}
			</Text>
			<Text size="small" color="color.text.subtle" maxLines={1}>
				{provider}
				{updatedDate && (
					<>
						<span aria-hidden="true">
							<Text size="small" color="color.text.subtle">
								{' · '}
							</Text>
						</span>
						<FormattedMessage {...messages.modified} />
						{isOlderThanWeek ? (
							<FormattedDate value={updatedDate} month="short" day="numeric" year="numeric" />
						) : (
							<FormattedRelativeTime
								value={-secondsAgo}
								unit="second"
								updateIntervalInSeconds={60}
							/>
						)}
						{modifiedBy && (
							<FormattedMessage {...messages.modifiedBy} values={{ author: modifiedBy }} />
						)}
					</>
				)}
			</Text>
		</Stack>
	</div>
);
